import { useContext, useEffect, useState } from 'react';
import { Button, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import BackButton from '../../../components/shared/actions/BackButton';
import SuppliersListPageContent from './components/SuppliersListPageContent';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const SuppliersPage = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();
  const [hasPermissionToCreateSupplier, setHasPermissionToCreateSupplier] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');

  useEffect(() => {
    setHasPermissionToCreateSupplier(hasPermissionTo(['SupplierCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterMarket(customerTypeIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');

  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  const handleCreateNew = () => {
    navigate('/procurement/suppliers/create');
  };

  const handleFilterChange = (instance: string, market: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const canViewSuppliers = hasPermissionTo(['SupplierView', 'ConfigurationAdmin']);

  if (!canViewSuppliers) {
    return null;
  }

  return (
    <SuppliersListPageContent
      onCreateNew={handleCreateNew}
      onFilterChange={handleFilterChange}
      initialInstance={currentFilterInstance}
      initialMarket={currentFilterMarket}
      initialSearchTerm={currentFilterSearchTerm}
    >
      {({ filterBar, headlineContent, table, isInitialLoading }) => (
        <ListPageTemplate
          title={t('pageTitle')}
          subtitle={t('subtitle')}
          loading={isInitialLoading}
          loadingLabel={t('loading')}
          backButton={(
            <BackButton
              handleClick={() => navigate('/settings')}
              title={t('backToSettings', 'Back to Settings')}
              isLink={false}
            />
          )}
          actions={hasPermissionToCreateSupplier && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
            >
              {t('createNew')}
            </Button>
          )}
          headlineContent={headlineContent}
          filterContent={filterBar}
          table={table}
        />
      )}
    </SuppliersListPageContent>
  );
};

export default SuppliersPage;
